import {Map, List, OrderedMap, fromJS, RecordOf} from 'immutable';
import {
  includes,
  flowRight as compose,
  trim,
  omit,
  flatten,
  sortBy,
} from 'lodash';
import {dynamoPageSlugPrefix} from '@packages/systems/core/constants/SharedConfig';
import {prop, when, notNil} from '@packages/utilities/fp/utils';
import {concat, propIs} from '@packages/systems/core/utils/ImmUtils';
// @ts-expect-error - TS7016 - Could not find a declaration file for module 'underscore.inflections'. '/home/runner/work/flow-to-typescript-codemod/flow-to-typescript-codemod/webflow/node_modules/underscore.inflections/lib/underscore.inflections.js' implicitly has an 'any' type.
import {singularize, pluralize, irregular} from 'underscore.inflections';
import {
  makeNonPersistedId,
  makeMintId,
  indexById,
  slugify,
  isMint,
} from '@packages/systems/core/utils/RecordUtils';
import {reifyField, type ReifyFieldInput} from '@packages/systems/core/render';
import moment from 'moment-timezone';
import {COLLECTION_TYPES} from '@packages/systems/dynamo/constants';
import {
  getCustomFields,
  isRequired,
  isSingleRef,
  isCustomField,
  NON_CONFIGURABLE_FIELD_SLUGS,
  NON_CUSTOM_FIELD_SLUGS,
  itemRefTargetCollectionId,
  getConfigurableFields,
} from '@packages/systems/dynamo/utils/CollectionFieldUtils';
import {titleize, capitalize} from '@packages/utilities/string-utils';
import {weakMemo, singleMemo} from '@packages/utilities/memo';
import type {
  CollectionsType,
  ItemCountByCollectionIdType,
} from '@packages/systems/core/records/CollectionStateType';
import CollectionRecord, {
  type CollectionRecordType as Collection,
  type CollectionRecordProps,
  type CollectionLocalizedSettings,
  CollectionRecordType,
} from '@packages/systems/dynamo/records/CollectionRecord';
import type {
  FieldProps,
  FieldType as Field,
  FieldType,
} from '@packages/systems/dynamo/records/Field';
import FieldGroup, {
  type FieldGroupType,
} from '@packages/systems/dynamo/records/FieldGroup';
import type {CollectionFieldGroup} from '@packages/systems/dynamo/types';
import {
  isCategoriesCollection,
  isProductsCollection,
  isProductDefaultSkuField,
  isSkusCollection,
  getProductsBindingFieldLabelSortValue,
  COMMERCE_ORDER_COLLECTIONS_IDS,
} from '@packages/systems/commerce/utils/CommerceCollectionUtils';
import {findPivot} from '@packages/systems/core/utils/NumberUtils';
import {type EmbedTokenOption} from '@packages/systems/core/utils/EmbedUtils/types';
import type {ObjectId} from 'bson';

type PresetType = {
  title: string;
  collection: {
    name: string;
    pluralName: string;
    singularName: string;
    slug: string;
    fields: Array<{
      id?: string;
      name?: string;
      slug?: string;
      editable?: boolean;
      required?: boolean;
      type?: string;
      helpText?: string;
      validations?: {
        singleLine: boolean;
      };
      innerType?: string;
    }>;
  };
};

irregular('cookie', 'cookies');
irregular('pie', 'pies');
irregular('brownie', 'brownies');
irregular('hippie', 'hippies');

export type RawCollection = Omit<
  Partial<CollectionRecordProps>,
  | 'fields'
  | 'fieldGroups'
  | 'localizedSettings'
  | 'itemCount'
  | 'pluralName'
  | '_id'
  | 'singularName'
  | 'sortPos'
  | 'createdOn'
  | 'lastUpdated'
> & {
  _id: ObjectId | string;
  pluralName?: string;
  singularName?: string | null; // Allow null from MongoDB/Mongoose sources
  sortPos?: number | null; // Allow null from MongoDB/Mongoose sources
  itemCount?: number;
  fields: readonly ReifyFieldInput[] | ReifyFieldInput[] | List<Field>;
  fieldGroups?: ReadonlyArray<CollectionFieldGroup> | List<FieldGroupType>;
  localizedSettings?: Record<string, any> | CollectionLocalizedSettings;
  createdOn?: Date | string | null; // Allow string from JSON/MongoDB sources
  lastUpdated?: Date | string | null; // Allow string from JSON/MongoDB sources
};

type SortableField = {
  label: string;
  type: string;
  value: string;
};

type CommerceOrderCollectionIdTuple = typeof COMMERCE_ORDER_COLLECTIONS_IDS;
type CommerceOrderCollectionId = CommerceOrderCollectionIdTuple[number];

type ReferenceField = {
  name: string;
  type: string;
  collectionId: string;
};

export type SortData = {field: string; direction: 'asc' | 'desc'};

const fullSlugField = {
  label: 'URL Path',
  value: JSON.stringify({path: 'fullSlug', type: 'FullSlug'}),
  type: 'FullSlug',
};

/*
 * In conjunction with the COLLECTION_TYPES enum, this function allows us
 * to determine a collection's type and handle special cases. For example, you'll
 * find several enums in CollectionFieldUtils.js that are based on collection types.
 *
 * We default to COLLECTIONS because historically CMS Collections came before
 * PRODUCTS, CATEGORIES, or SKUS collections and significant amounts of app functionality
 * work on the assumption that a collection is of type COLLECTIONS.
 */
export const getCollectionType = (collection: Collection) => {
  switch (true) {
    case isProductsCollection(collection):
      return COLLECTION_TYPES.PRODUCTS;

    case isCategoriesCollection(collection):
      return COLLECTION_TYPES.CATEGORIES;

    case isSkusCollection(collection):
      return COLLECTION_TYPES.SKUS;

    default:
      return COLLECTION_TYPES.CMS_COLLECTIONS;
  }
};

export function reifyCollection(raw: RawCollection): CollectionRecordType {
  return CollectionRecord({
    ...omit(raw, ['fields', 'fieldGroups', 'localizedSettings']),
    pluralName: raw.name,
    fields: List(raw.fields.map(reifyField)),
    fieldGroups: reifyFieldGroups(raw.fieldGroups),
    localizedSettings: fromJS(raw.localizedSettings ?? {}), // we apply `fromJS(…)` here, because want a deep Immutable.js Map here instead of a plan JS object
  } as Partial<CollectionRecordProps>);
}

function reifyFieldGroups(
  raw: RawCollection['fieldGroups']
): List<FieldGroupType> {
  if (!raw) {
    return List();
  }
  if (List.isList(raw)) {
    return raw as List<FieldGroupType>;
  }
  return List(
    raw.map((group) =>
      FieldGroup({
        displayName: group.displayName,
        fieldIds: List(group.fieldIds ?? []),
        description: group.description,
      })
    )
  );
}

export const reifyCollections = <T extends RawCollection>(
  collections: Array<T>
): Map<string, CollectionRecordType> => indexById(collections, reifyCollection);

export function mintCollection(sortPos: number) {
  return new CollectionRecord({
    _id: makeMintId(),
    fields: List.of(
      reifyField({
        id: makeNonPersistedId(),
        name: 'Name',
        slug: 'name',
        type: 'PlainText',
      }),
      reifyField({
        id: makeNonPersistedId(),
        name: 'Slug',
        slug: 'slug',
        type: 'PlainText',
      })
    ),
    sortPos,
  });
}

export function fromPreset(preset: PresetType, sortPos: number) {
  const {name, slug, singularName, pluralName, fields} = preset.collection;
  return mintCollection(sortPos)
    .set('name', name)
    .set('pluralName', pluralName)
    .set('singularName', singularName)
    .set('slug', slug)
    .update(
      'fields',
      concat(List(fields.map(reifyField)).map(fieldFromPreset))
    );
}

const fieldFromPreset = (field: RecordOf<FieldProps>) => {
  return field.set('id', makeNonPersistedId()).set('editable', true);
};

export const getTotalNumberOfItems = (
  itemCountByCollectionId: ItemCountByCollectionIdType
): number => {
  return itemCountByCollectionId.reduce((total, count) => {
    return total + count;
  }, 0);
};

// Get the first empty referenced collection
export function getEmptyReferencedCollection(
  collection: Collection,
  collections: CollectionsType,
  itemCountByCollectionId: ItemCountByCollectionIdType
): CollectionRecordType | null {
  const requiredRefFieldCollections = getRequiredCustomFields(
    collection
  ).reduce<string[]>((memo, field) => {
    const referencedCollectionId = itemRefTargetCollectionId(field);
    if (referencedCollectionId !== null) {
      memo.push(referencedCollectionId);
    }
    return memo;
  }, []);

  const emptyReferencedCollId = requiredRefFieldCollections.find(
    (collectionId) => {
      return itemCountByCollectionId.get(collectionId) === 0;
    }
  );

  return emptyReferencedCollId ? collections.get(emptyReferencedCollId) : null;
}

export function getRequiredCustomFields(collection: Collection) {
  return getCustomFields(collection.get('fields')).filter(isRequired);
}

const getName = prop('name');
const defaultTo = (defaultValue: string) => (value: string) =>
  value || defaultValue;
const singularizedName = compose(
  defaultTo('Item'),
  capitalize,
  singularize,
  trim,
  getName
);
const pluralizedName = compose(
  defaultTo('Items'),
  capitalize,
  pluralize,
  trim,
  getName
);

export function fillSingularName(collection: Collection): Collection {
  return collection.set('singularName', singularizedName(collection));
}

export function fillPluralName(collection: Collection): Collection {
  return collection.set('pluralName', pluralizedName(collection));
}

const slugifyName = compose(slugify, getName);

export function fillSlug(collection: Collection): Collection {
  return collection.set('slug', slugifyName(collection));
}

export const fillNames = compose(fillSingularName, fillPluralName);

export function getDefaultName({
  item,
  collection,
}: {
  item: Map<string, any>;
  collection: Collection;
}) {
  return item.get('name') || `New ${titleize(collection.singularName)}`;
}

const generateSingleFieldTokenOptions = (
  collection: Collection,
  singleOptionPath: Array<string>
): EmbedTokenOption[] => {
  const singleOptionSlug =
    singleOptionPath.length > 1 ? singleOptionPath[1] : singleOptionPath[0];
  const field = collection.fields.find(propIs('slug', singleOptionSlug));
  if (field) {
    const type = field.innerType || field.type;
    const valueObj = {
      path:
        singleOptionPath.length > 1
          ? singleOptionPath[0] + ':' + field.slug
          : field.slug,
      type,
    } as const;
    return [
      {
        label: field.name,
        value: JSON.stringify(valueObj),
        type,
      },
    ];
  } else {
    return [];
  }
};

function isCommerceOrderCollectionId(
  value: string
): value is CommerceOrderCollectionId {
  return COMMERCE_ORDER_COLLECTIONS_IDS.includes(
    value as CommerceOrderCollectionId
  );
}

/**
 * Returns input token options for given collection
 * @param  {Object}   collection   Collection whose input token options are wanted
 * @param  {Object[]} collections  CollectionStore.collections
 * @param  {Boolean}  narrow       Flag defining if token options shouldn't have fields of itemRefs or fields of items in itemRefSets
 * @param  {String | null} fieldRepeaterSlug  Slug to filter the multi image field from the other fields
 * @return {Object[]} Sorted array of options objects, eg [{label: 'Name', type: 'PlainText', value: 'name'}, {label: 'Author: Name', type: 'PlainText', value: 'author:name'}]
 */
export function getFieldTokenOptions(
  collection: Collection | null | undefined,
  collections: CollectionsType,
  {
    singleOption,
    isPageSettings = false,
  }: {
    singleOption?: Array<string> | null;
    isPageSettings?: boolean;
  } = {}
): EmbedTokenOption[] | null {
  if (!collection) {
    // we are on a static page
    return null;
  }

  if (isCommerceOrderCollectionId(collection._id)) {
    // SSR never has the Ecommerce Order data, so it renders all the Order
    // bindings as empty strings. Meanwhile, Ecommerce site module does not
    // re-evaluate token bindings. Combining the two, Order token bindings never
    // show up on the published sites. Therefore, we want to disable adding
    // token bindings inside Order binding contexts.
    return null;
  }

  if (singleOption) {
    if (singleOption.length === 1) {
      return generateSingleFieldTokenOptions(collection, singleOption);
    } else {
      const itemRefSlug = singleOption[0];
      const itemRefField = collection.fields.find(
        (f) => f.slug === itemRefSlug
      );
      const itemRefCollectionId = itemRefTargetCollectionId(itemRefField);
      const itemRefCollection =
        itemRefCollectionId === null
          ? null
          : collections.get(itemRefCollectionId);
      if (itemRefCollection) {
        return generateSingleFieldTokenOptions(itemRefCollection, singleOption);
      }
      return null;
    }
  }

  return _memoizedGetFieldTokenOptions(collection)(collections)(isPageSettings);
}

const _memoizedGetFieldTokenOptions = weakMemo((collection: Collection) =>
  weakMemo((collections: CollectionsType) =>
    singleMemo((isPageSettings: boolean): EmbedTokenOption[] => {
      const collectionType = getCollectionType(collection);
      const denyList = [
        '_draft',
        '_archived',
        '_noSearch',
        '_created_by',
        '_updated_by',
        '_published_by',
      ];
      const textFieldFilter = (field: FieldType) =>
        includes(
          [
            'PlainText',
            'Number',
            'Link',
            'Bool',
            'Option',
            'Color',
            'ImageRef',
            'FileRef',
            'Email',
            'Phone',
            'HighlightedText',
            'CommercePrice',
          ],
          field.type
        ) && !includes(denyList, field.slug);
      const dateFieldFilter = (field: FieldType) => field.type === 'Date';

      // Token options from item's fields
      const textFields = collection.fields
        .toArray()
        .filter(textFieldFilter)
        .map((field) => {
          const valueObj = {path: field.slug, type: field.type} as const;
          return {
            label: field.name,
            value: JSON.stringify(valueObj),
            type: field.type,
          };
        });

      const dateFields = collection.fields
        .toArray()
        .filter(dateFieldFilter)
        .map((field) => {
          const valueObj = {
            path: field.slug,
            transformers: [{name: 'date', arguments: ['MMM DD, YYYY']}],
            type: field.type,
          } as const;
          return {
            label: field.name,
            value: JSON.stringify(valueObj),
            type: field.type,
          };
        });

      // Token options from item's item references
      const itemRefFields = collection.fields
        .toArray()
        .filter(isSingleRef)
        .map((field1) => {
          const itemRefSlug = field1.slug;
          const itemRefCollectionId = itemRefTargetCollectionId(field1);
          const itemRefCollection =
            itemRefCollectionId === null
              ? null
              : collections.get(itemRefCollectionId);

          // Use additional filtering and different labeling for Default SKU fields
          const itemRefTextFields = itemRefCollection
            ? itemRefCollection.fields
                .toArray()
                .filter(textFieldFilter)
                .filter((field) =>
                  isProductDefaultSkuField(field1, collections)
                    ? isCustomField(field, collectionType)
                    : field
                )
                .map((field2) => {
                  const valueObj = {
                    path: itemRefSlug + ':' + field2.slug,
                    type: field2.type,
                  } as const;

                  const label = isProductDefaultSkuField(field1, collections)
                    ? field2.name
                    : field1.name + ': ' + field2.name;

                  return {
                    label,
                    value: JSON.stringify(valueObj),
                    type: field2.type,
                  };
                })
            : [];

          // Use additional filtering and different labeling for Default SKU fields
          const itemRefDateFields = itemRefCollection
            ? itemRefCollection.fields
                .toArray()
                .filter(dateFieldFilter)
                .filter((field) =>
                  isProductDefaultSkuField(field1, collections)
                    ? isCustomField(field, collectionType)
                    : field
                )
                .map((field2) => {
                  const valueObj = {
                    path: itemRefSlug + ':' + field2.slug,
                    transformers: [{name: 'date', arguments: ['MMM DD, YYYY']}],
                    type: field2.type,
                  } as const;

                  const label = isProductDefaultSkuField(field1, collections)
                    ? field2.name
                    : field1.name + ': ' + field2.name;

                  return {
                    label,
                    value: JSON.stringify(valueObj),
                    type: field2.type,
                  };
                })
            : [];

          return itemRefTextFields.concat(itemRefDateFields);
        });

      // We want to show fields in consistent order
      const fieldTypeOrder = [
        'PlainText',
        'Number',
        'Link',
        'HighlightedText',
        'Date',
        'Option',
        'Bool',
        'Color',
        'ImageRef',
        'FileRef',
        'CommercePrice',
      ];
      // But at the same time we want to show default fields as first ones
      const nonCustomFieldOrder = Object.keys(
        NON_CUSTOM_FIELD_SLUGS[collectionType]
      );
      const nonConfigurableFieldsOrder = Object.keys(
        NON_CONFIGURABLE_FIELD_SLUGS
      );

      const fieldTypeSorter = (field: SortableField) => {
        const index = fieldTypeOrder.indexOf(field.type);
        return index === -1 ? fieldTypeOrder.length : index;
      };

      const fieldSlugSorter = (
        comparisonGetter: (field: SortableField) => string
      ) => {
        const defaultFieldsLength =
          nonCustomFieldOrder.length + nonConfigurableFieldsOrder.length;

        return (field: SortableField) => {
          const comparison = comparisonGetter(field);
          const nonCustomFieldIndex = nonCustomFieldOrder.indexOf(comparison);
          if (nonCustomFieldIndex === -1) {
            const nonConfigurableFieldIndex =
              nonConfigurableFieldsOrder.indexOf(comparison);
            if (nonConfigurableFieldIndex === -1) {
              return defaultFieldsLength;
            } else {
              return defaultFieldsLength + nonConfigurableFieldIndex;
            }
          } else {
            return nonCustomFieldIndex;
          }
        };
      };

      const sortedItemRefFields = flatten(
        itemRefFields.map((fields) => {
          return sortBy(fields, [
            fieldSlugSorter(
              (field) =>
                JSON.parse(field.value).path.split(':')[1].split('.')[0]
            ),
            fieldTypeSorter,
          ]);
        })
      );

      // Use special sorting for product collection
      if (isProductsCollection(collection)) {
        return sortBy(
          textFields
            .concat(dateFields)
            .concat(sortedItemRefFields)
            .concat(isPageSettings ? [] : [fullSlugField]),
          (field) => getProductsBindingFieldLabelSortValue(field.label)
        );
      }

      const sortedItemFields = sortBy(
        textFields
          .concat(dateFields)
          .concat(isPageSettings ? [] : [fullSlugField]),
        [
          fieldSlugSorter(
            (field) => JSON.parse(field.value).path.split('.')[0]
          ),
          fieldTypeSorter,
        ]
      );

      return sortedItemFields.concat(sortedItemRefFields);
    })
  )
);

export const getFieldBySlug = (
  collection: Collection,
  slug: string
): Field | null => collection.fields.find((f: Field) => f.slug === slug);

export const getFieldIdBySlug = (
  collection: Collection,
  slug: string
): string => {
  const found = getFieldBySlug(collection, slug);
  if (!found) {
    return '';
  }
  return found.id;
};

export const sortByDateAsc = (collections: CollectionsType): CollectionsType =>
  collections.sortBy(
    // new Date will allow sort mint collection items properly as well
    (collection) => collection.createdOn || new Date().toString()
  );

export const sortByNameDesc = (collections: CollectionsType): CollectionsType =>
  collections.sortBy((collection) => collection.name).reverse();

export const isNewCollection = (
  collection: Collection,
  lastPublished?: string | null
) => {
  if (!lastPublished) {
    // If lastPublished is null, all the comparisons with
    // moment.utc(lastPublished) will return false.
    return true;
  }
  const createdOn = collection.get('createdOn');
  if (createdOn) {
    return moment.utc(createdOn) > moment.utc(lastPublished);
  }
  return false;
};

export const collectionSchemaChangedSinceLastPublish = (
  collection: Collection,
  lastPublished?: string | null
) => {
  if (!lastPublished) {
    // If lastPublished is null, all the comparisons with
    // moment.utc(lastPublished) will return false.
    return true;
  }
  const lastUpdated = collection.get('lastUpdated');
  if (lastUpdated) {
    return moment.utc(lastUpdated) > moment.utc(lastPublished);
  }
  return false;
};

export const getCollectionSlugById = (
  collectionId: string,
  collections: CollectionsType
) => collections.getIn([collectionId, 'slug']);

export const getCollectionSlugMap = weakMemo(
  (collections: CollectionsType): Record<string, string> => {
    return collections.reduce<Record<string, string>>(
      (collectionSlugMap, collection, collectionId) => {
        if (collection.slug !== null) {
          collectionSlugMap[collectionId] = collection.slug;
        }
        return collectionSlugMap;
      },
      {}
    );
  }
);

// Opaque type removed - see /packages/utilities/type-safety/README.md for more information
export type SortedCollectionMap = OrderedMap<string, Collection>;

export const sortCollectionsBySortPos = weakMemo(
  (collections: CollectionsType): SortedCollectionMap => {
    return collections.toOrderedMap().sortBy(getSortPos);
  }
);

export const getSortPos = (c: Collection) => {
  const setSortPos = c.sortPos;
  // If the sortPos is not set or negative somehow, return the createdOn time as the sortPos
  if (!setSortPos || setSortPos < 0) {
    // There is a very rare chance that createdOn is unset, but to make flow happy, I added the extra check
    return c.createdOn ? new Date(c.createdOn).getTime() : Date.now();
  }
  return setSortPos;
};

export const getNextSortPos = (collections: CollectionsType) => {
  const sortedCollections = sortCollectionsBySortPos(collections);
  return sortedCollections.size > 0
    ? findPivot(getSortPos(sortedCollections.toIndexedSeq().get(0)), 0)
    : Number.MAX_SAFE_INTEGER;
};

type CollectionsWithReferenceFields = {
  collection: Collection;
  fieldNames: string;
};

export const getPeerCollections = (
  collections: CollectionsType,
  currentCollection: Collection
): CollectionsWithReferenceFields[] => {
  const currentCollectionId = currentCollection.get('_id');
  return collections.reduce<Array<CollectionsWithReferenceFields>>(
    (collectionsWithRefFields, collection) => {
      const refCollectionId = collection.get('_id');
      if (currentCollectionId === refCollectionId) {
        return collectionsWithRefFields;
      }
      const fieldNames = collection
        .get('fields')
        .reduce<Array<string>>((names, field) => {
          const refId = field.validations.get('collectionId');
          if (
            (field.type === 'ItemRef' || field.type === 'ItemRefSet') &&
            refId === currentCollectionId
          ) {
            names.push(field.name);
          }
          return names;
        }, []);

      refCollectionId &&
        fieldNames.length &&
        collectionsWithRefFields.push({
          collection,
          fieldNames: fieldNames.join(', '),
        });
      return collectionsWithRefFields;
    },
    []
  );
};

// get all the reference fields a collection has
export const getReferenceFields = (
  collection: Collection
): ReferenceField[] => {
  const refCollections = collection
    .get('fields')
    .reduce<ReferenceField[]>((refFields, field) => {
      const collectionId = itemRefTargetCollectionId(field);
      if (collectionId !== null) {
        refFields.push({
          name: field.name,
          type: field.type,
          collectionId,
        });
      }
      return refFields;
    }, []);
  return refCollections;
};

export function getCollectionNameFromId(
  collections: CollectionsType,
  id?: string | null
): string | null | undefined {
  return collections.getIn([id, 'name']);
}

type CollectionSlug = string;

// the FE and BE types of Collection differ, but they will all have at least name and slug which is what we care about here
type IsomorphicCollection = {
  name: string;
  slug: string;
  localizedSettings?: {
    [localeId: string]: {
      slug: string;
    };
  };
};

export function getCollectionSlug(
  collection: IsomorphicCollection,
  cmsLocaleId?: string | null
): CollectionSlug {
  if (!collection) {
    throw new Error('No collection provided.');
  }

  if (!cmsLocaleId || !collection.slug) {
    return collection.slug;
  }

  return collection?.localizedSettings?.[cmsLocaleId]?.slug ?? collection.slug;
}

/**
 * This function returns the root path where the detail page lives on S3, such as:
 * /detail_post
 */
export function getCollectionDetailPagePath(
  collection: IsomorphicCollection
): CollectionSlug {
  if (!collection) {
    throw new Error('No collection provided.');
  }

  return `/${dynamoPageSlugPrefix}${getCollectionSlug(collection)}`;
}
/**
 * This function returns if the collection has any rich text fields
 */
export function hasRichTextFields(collection: CollectionRecordType) {
  return collection.fields.some((field) => field.type === 'RichText');
}

export function asJSON(collection: CollectionRecordType) {
  return (
    collection
      .update(
        'fields',
        compose(
          fieldsWithoutNullValidations,
          fieldsWithoutMintIds,
          fieldsWithoutTypes,
          getConfigurableFields
        )
      )
      .update('name', (name) => collection.get('pluralName', name))
      // @ts-expect-error - TS2339 - Property 'toMap' does not exist on type 'CollectionRecordType'.
      .toMap()
      .toJSON()
  );
}

const rejectNil = (validations: FieldProps['validations']) =>
  validations.filter(notNil);

function fieldsWithoutNullValidations(fields: CollectionRecordType['fields']) {
  return fields.map((f) => f.update('validations', when(notNil)(rejectNil)));
}

function fieldsWithoutMintIds(fields: CollectionRecordType['fields']) {
  return fields.map((f) => (isMint(f) ? f.remove('id') : f));
}

function fieldsWithoutTypes(fields: CollectionRecordType['fields']) {
  return fields.filter((field) => Boolean(field.get('type')));
}
